import React, { useState, useEffect } from 'react';
import { invoiceAPI, companyAPI, userAPI, projectAPI, paymentAccountAPI, razorpayAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';

// ── Constants ────────────────────────────────────────────────────────────────
const PLATFORMS = {
  upwork_personal: { label:'Upwork Personal', icon:'🟢', color:'#14a800', bg:'#e6f9e6', currency:'USD', desc:'Your personal Upwork profile' },
  upwork_company:  { label:'Upwork Company',  icon:'🏢', color:'#0d7a00', bg:'#d4f5d4', currency:'USD', desc:'Company Upwork profile' },
  wise:            { label:'Wise',            icon:'💙', color:'#163300', bg:'#c6f135', currency:'USD', desc:"Wife's Wise account" },
  paypal:          { label:'PayPal',          icon:'💳', color:'#003087', bg:'#e8f0fe', currency:'USD', desc:'Your PayPal account' },
  local_gst:       { label:'Local (GST)',     icon:'🧾', color:'#d97706', bg:'#fef3c7', currency:'INR', desc:'Indian client with GST → Company account' },
  local_nogst:     { label:'Local (No GST)',  icon:'📋', color:'var(--pm-primary)', bg:'#ede9fe', currency:'INR', desc:'Indian client, no GST → Personal account' },
  bank_transfer:   { label:'Bank Transfer',   icon:'🏦', color:'#0f766e', bg:'#ccfbf1', currency:'INR', desc:'Direct NEFT/RTGS → Personal account' },
  razorpay:        { label:'Razorpay',        icon:'💎', color:'#2d6be4', bg:'#e8f0fe', currency:'INR', desc:'Online payment via Razorpay checkout' },
  other:           { label:'Other',           icon:'📎', color:'#64748b', bg:'#f1f5f9', currency:'USD', desc:'Other platform' },
};

const CURRENCIES = ['USD','GBP','EUR','AUD','CAD','INR','SGD','AED','NZD'];

const STATUS_CFG = {
  draft:     { label:'Draft',     bg:'#f1f5f9', color:'#475569' },
  sent:      { label:'Sent',      bg:'#dbeafe', color:'#1e40af' },
  viewed:    { label:'Viewed',    bg:'#ede9fe', color:'#5b21b6' },
  paid:      { label:'Paid',      bg:'#dcfce7', color:'#14532d' },
  partial:   { label:'Partial',   bg:'#fef9c3', color:'#713f12' },
  overdue:   { label:'Overdue',   bg:'#fee2e2', color:'#991b1b' },
  cancelled: { label:'Cancelled', bg:'#f1f5f9', color:'#94a3b8' },
};

const TAX_TYPES = [
  { v:'none',  l:'No Tax' },
  { v:'gst',   l:'GST (CGST + SGST)' },
  { v:'igst',  l:'IGST (Interstate)' },
  { v:'vat',   l:'VAT' },
  { v:'other', l:'Other Tax' },
];

const UPWORK_FEES = [{ v:0,l:'0%'},{v:5,l:'5% (>$10k)'},{v:10,l:'10% ($500-$10k)'},{v:20,l:'20% (first $500)'}];

const FMT = (n, cur='USD') => {
  const sym = { USD:'$', GBP:'£', EUR:'€', AUD:'A$', INR:'₹', CAD:'C$', SGD:'S$', AED:'AED ', NZD:'NZ$' }[cur] || cur+' ';
  return sym + (n||0).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 });
};
const FMTD = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const EMPTY_LINE = { description:'', quantity:1, unit:'hrs', rate:0, amount:0 };

const EMPTY_FORM = {
  platform:'upwork_personal',
  paymentAccountId: '', paymentAccountSnapshot: {}, currency:'USD', exchangeRate:1, invoiceNumber:'',
  client:'', clientSnapshot:{ name:'', email:'', company:'', address:'', country:'', gstNumber:'' },
  project:'',
  lineItems:[{ ...EMPTY_LINE }],
  discountPct:0, taxType:'none', taxRate:18,
  issueDate: new Date().toISOString().slice(0,10),
  dueDate: new Date(Date.now()+30*86400000).toISOString().slice(0,10),
  notes:'Thank you for your business!',
  terms:'Payment due within 30 days.',
  upworkFeePercent:10, upworkContractId:'', upworkJobTitle:'',
  wiseTransferId:'', platformRefId:'',
  status:'draft',
};

// ── Inline styles ─────────────────────────────────────────────────────────────
const INP = (extra={}) => ({ width:'100%', padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:"'Outfit',sans-serif", background:'#fafbfc', transition:'border .2s', ...extra });
const S = {
  page:    { maxWidth:1400, margin:'0 auto', fontFamily:"'Outfit',sans-serif", paddingBottom:60 },
  card:    { background:'#fff', borderRadius:18, boxShadow:'0 2px 20px #00000009', border:'1px solid #f0f4f8' },
  btn:     (v) => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7, transition:'all .18s',
    ...(v==='primary'  ? { background:'linear-gradient(135deg,#0f172a,#1e293b)', color:'#fff', boxShadow:'0 4px 14px #0f172a2a' }
      : v==='success'  ? { background:'#dcfce7', color:'#14532d' }
      : v==='danger'   ? { background:'#fee2e2', color:'#dc2626' }
      : v==='ghost'    ? { background:'transparent', color:'#475569', border:'1.5px solid #e2e8f0' }
      :                  { background:'#f1f5f9', color:'#64748b' }) }),
  th:      { padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'2px solid #f1f5f9', whiteSpace:'nowrap' },
  td:      { padding:'13px 14px', fontSize:13, borderBottom:'1px solid #f8fafc', color:'#475569', verticalAlign:'middle' },
  label:   { display:'block', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:5 },
  section: { fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', padding:'8px 0', marginBottom:12, borderBottom:'1px solid #f1f5f9' },
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function Invoices() {
  const { user }  = useAuth();
  const confirm = useConfirm();
  const [view,        setView]        = useState('list');   // list | form | detail
  const [invoices,    setInvoices]    = useState([]);
  const [stats,       setStats]       = useState({});
  const [clients,     setClients]     = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [company,     setCompany]     = useState({});
  const [loading,     setLoading]     = useState(true);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [editId,      setEditId]      = useState(null);
  const [selected,    setSelected]    = useState(null);
  const [filterPlat,  setFilterPlat]  = useState('');
  const [filterStatus,setFilterStatus]= useState('');
  const [filterCur,   setFilterCur]   = useState('');
  const [payModal,    setPayModal]    = useState(null);
  const [payForm,     setPayForm]     = useState({ status:'paid', paymentMethod:'', paymentNotes:'', paidOn: new Date().toISOString().slice(0,10) });
  const [ppModal,     setPpModal]     = useState(false);
  const [ppForm,      setPpForm]      = useState({ amount:'', method:'', note:'', date: new Date().toISOString().slice(0,10) });
  const [ppSaving,    setPpSaving]    = useState(false);

  const [payAccounts,   setPayAccounts]   = useState([]);
  const [rzpProcessing, setRzpProcessing] = useState(false);

  // ── Load Razorpay checkout script once ────────────────────────────────────
  useEffect(() => {
    if (!document.getElementById('razorpay-script')) {
      const s = document.createElement('script');
      s.id  = 'razorpay-script';
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  // ── Pay via Razorpay ───────────────────────────────────────────────────────
  const payViaRazorpay = async (invoice) => {
    try {
      setRzpProcessing(true);
      const cfgR = await razorpayAPI.config();
      if (!cfgR.data.configured) {
        toast.error('Razorpay keys not configured. Add them in backend .env file.');
        setRzpProcessing(false); return;
      }

      const orderR = await razorpayAPI.createOrder(invoice._id || invoice.id);
      const { order } = orderR.data;
      const { invoice: inv } = orderR.data;

      const options = {
        key:         cfgR.data.keyId,
        amount:      order.amount,
        currency:    order.currency,
        name:        'Invoice Payment',
        description: `Invoice #${inv.invoiceNumber}`,
        order_id:    order.id,
        prefill: {
          name:  inv.clientSnapshot?.name  || '',
          email: inv.clientSnapshot?.email || '',
        },
        theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--pm-primary').trim() || '#6366f1' },
        handler: async (response) => {
          try {
            await razorpayAPI.verify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              invoiceId:           inv.id,
            });
            toast.success('Payment successful! Invoice marked as paid.');
            load();
            if (selected?._id === inv.id || selected?.id === inv.id) {
              setSelected(s => ({ ...s, status: 'paid', paymentMethod: 'Razorpay' }));
            }
          } catch (e) {
            toast.error('Payment verification failed: ' + (e.response?.data?.message || e.message));
          }
        },
        modal: { ondismiss: () => setRzpProcessing(false) },
      };

      if (!window.Razorpay) { toast.error('Razorpay script not loaded yet. Please try again.'); setRzpProcessing(false); return; }
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setRzpProcessing(false);
    }
  };

  useEffect(() => {
    load();
    companyAPI.get().then(r => setCompany(r.data.companySettings||{})).catch(()=>{});
    userAPI.getAll().then(r => {
      const clientList = (r.data.users||[]).filter(u => u.role==='client');
      setClients(clientList);
    }).catch(e => console.error('Failed to load clients:', e?.response?.data?.message || e.message));
    projectAPI.getAll().then(r => setProjects(r.data.projects||[])).catch(()=>{});
    paymentAccountAPI.list().then(r => setPayAccounts(r.data.accounts||[])).catch(()=>{});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await invoiceAPI.list({ platform: filterPlat||undefined, status: filterStatus||undefined, currency: filterCur||undefined });
      setInvoices(r.data.invoices||[]);
      setStats(r.data.stats||{});
    } catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  };

  useEffect(() => { if (view==='list') load(); }, [filterPlat, filterStatus, filterCur]);

  // ── Auto-fill invoice number when platform changes ────────────────────────
  const handlePlatformChange = async (platform) => {
    const plat = PLATFORMS[platform];
    const isUpwork = platform.startsWith('upwork');
    setForm(f => ({ ...f, platform, currency: plat.currency,
      taxType: platform === 'local_gst' ? 'gst' : 'none',
      // Upwork: clear account so user picks manually each time
      ...(isUpwork ? { paymentAccountId: '', paymentAccountSnapshot: {} } : {}),
    }));
    try {
      if (isUpwork) {
        // Only fetch invoice number for Upwork, no account auto-suggest
        const numR = await invoiceAPI.nextNumber(platform);
        if (!editId) setForm(f => ({ ...f, invoiceNumber: numR.data.number }));
      } else {
        const [numR, accR] = await Promise.all([
          invoiceAPI.nextNumber(platform),
          paymentAccountAPI.suggestForPlatform(platform),
        ]);
        const acc = accR.data.account;
        setForm(f => ({
          ...f,
          ...(!editId ? { invoiceNumber: numR.data.number } : {}),
          paymentAccountId: acc?._id || '',
          paymentAccountSnapshot: acc ? buildAccSnapshot(acc) : {},
        }));
      }
    } catch {}
  };

  // Helper: build snapshot object from an account record
  const buildAccSnapshot = (acc) => ({
    name: acc.name, owner: acc.owner, accountType: acc.type,
    bankName: acc.bankName, accountHolder: acc.accountHolder,
    accountNumber: acc.accountNumber, ifscCode: acc.ifscCode,
    branchName: acc.branchName, upiId: acc.upiId,
    email: acc.paypalEmail || acc.wiseEmail || '',
    wiseEmail: acc.wiseEmail, paypalEmail: acc.paypalEmail,
    wiseCurrency: acc.wiseCurrency,
    gstNumber: acc.gstNumber, panNumber: acc.panNumber,
    currency: acc.wiseCurrency || acc.primaryCurrency || 'INR',
  });

  // ── Line item helpers ─────────────────────────────────────────────────────
  const setLine = (idx, field, val) => {
    setForm(f => {
      const items = [...f.lineItems];
      items[idx] = { ...items[idx], [field]: field==='quantity'||field==='rate' ? parseFloat(val)||0 : val };
      items[idx].amount = +(items[idx].quantity * items[idx].rate).toFixed(2);
      return { ...f, lineItems: items };
    });
  };
  const addLine    = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { ...EMPTY_LINE }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_,i) => i!==idx) }));

  // ── Computed totals (live) ────────────────────────────────────────────────
  const subtotal = form.lineItems.reduce((s,i) => s + (i.quantity||0)*(i.rate||0), 0);
  const discountAmount = +(subtotal * (form.discountPct||0) / 100).toFixed(2);
  const afterDiscount  = subtotal - discountAmount;
  let cgst=0, sgst=0, igst=0, otherTax=0;
  if (form.taxType==='gst')   { cgst=+(afterDiscount*(form.taxRate||0)/200).toFixed(2); sgst=cgst; }
  if (form.taxType==='igst')  { igst=+(afterDiscount*(form.taxRate||0)/100).toFixed(2); }
  if (['vat','other'].includes(form.taxType)) { otherTax=+(afterDiscount*(form.taxRate||0)/100).toFixed(2); }
  const totalTax = cgst+sgst+igst+otherTax;
  const total    = +(afterDiscount + totalTax).toFixed(2);
  const upworkFee= form.platform==='upwork' ? +(total*(form.upworkFeePercent||0)/100).toFixed(2) : 0;
  const upworkNet= total - upworkFee;

  // ── Open new form ──────────────────────────────────────────────────────────
  const openNew = async () => {
    const f = { ...EMPTY_FORM, issueDate: new Date().toISOString().slice(0,10), dueDate: new Date(Date.now()+30*86400000).toISOString().slice(0,10) };
    try { const r = await invoiceAPI.nextNumber('upwork'); f.invoiceNumber = r.data.number; } catch {}
    setForm(f); setEditId(null); setView('form');
  };

  const openEdit = (inv) => {
    setForm({
      ...EMPTY_FORM, ...inv,
      client: String(inv.clientId || inv.client?._id || inv.client?.id || inv.client || ''),
      project: inv.project?._id || inv.project || '',
      issueDate: inv.issueDate?.slice(0,10) || '',
      dueDate:   inv.dueDate?.slice(0,10)   || '',
    });
    setEditId(inv._id);
    setView('form');
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async (status) => {
    if (!form.invoiceNumber) return toast.error('Invoice number required');
    if (!form.lineItems.length || form.lineItems.every(i => !i.description)) return toast.error('Add at least one line item');
    try {
      const payload = { ...form, status: status||form.status };
      if (editId) { await invoiceAPI.update(editId, payload); toast.success('Invoice updated!'); }
      else        { await invoiceAPI.create(payload);         toast.success('Invoice created!'); }
      setView('list'); load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to save'); }
  };

  // ── Mark payment ──────────────────────────────────────────────────────────
  const markPayment = async () => {
    try {
      await invoiceAPI.updateStatus(payModal, { ...payForm, status: 'paid' });
      toast.success('Invoice marked as paid!');
      setPayModal(null);
      load();
      if (selected?._id === payModal) setSelected(s => ({ ...s, status: 'paid', ...payForm }));
    } catch { toast.error('Failed to record payment'); }
  };

  const deleteInvoice = async (id) => {
    const ok = await confirm('Delete this invoice?', { title: 'Delete Invoice?', variant: 'danger' });
    if (!ok) return;
    try { await invoiceAPI.remove(id); toast.success('Deleted'); load(); if (selected?._id===id) setView('list'); }
    catch { toast.error('Failed'); }
  };

  const savePartialPayment = async () => {
    if (!ppForm.amount || parseFloat(ppForm.amount) <= 0) return toast.error('Enter valid amount');
    setPpSaving(true);
    try {
      const r = await invoiceAPI.addPartialPayment(selected._id, ppForm);
      setSelected(r.data.invoice);
      setPpModal(false);
      setPpForm({ amount:'', method:'', note:'', date: new Date().toISOString().slice(0,10) });
      load();
      toast.success('Payment recorded!');
    } catch { toast.error('Failed to record payment'); }
    setPpSaving(false);
  };

  // ── Print invoice ─────────────────────────────────────────────────────────
  const printInvoice = (inv) => {
    const co   = company;
    const plat = PLATFORMS[inv.platform] || PLATFORMS.other;
    const cur  = inv.currency || 'USD';
    const fmt  = (n) => FMT(n, cur);

    const logoHtml = co.logoBase64
      ? `<img src="${co.logoBase64}" alt="logo" style="max-height:70px;max-width:200px;object-fit:contain;display:block;"/>`
      : `<div style="font-size:26px;font-weight:900;color:#0f172a">${co.name || 'Your Company'}</div>`;

    const gstBadge = inv.platform === 'local_gst'
      ? `<div style="display:inline-block;background:#fff4f0;color:#ff6b35;border:1.5px solid #ffcdb2;border-radius:8px;padding:4px 12px;font-size:11px;font-weight:700;margin-top:6px">GST INVOICE</div>`
      : '';

    const taxRows = () => {
      if (inv.taxType === 'gst')  return `<tr><td>CGST (${inv.taxRate/2}%)</td><td style="text-align:right">${fmt(inv.cgst)}</td></tr><tr><td>SGST (${inv.taxRate/2}%)</td><td style="text-align:right">${fmt(inv.sgst)}</td></tr>`;
      if (inv.taxType === 'igst') return `<tr><td>IGST (${inv.taxRate}%)</td><td style="text-align:right">${fmt(inv.igst)}</td></tr>`;
      if (['vat','other'].includes(inv.taxType)) return `<tr><td>Tax (${inv.taxRate}%)</td><td style="text-align:right">${fmt(inv.otherTax)}</td></tr>`;
      return '';
    };

    const acc = inv.paymentAccountSnapshot || {};
    const upiId = acc.upiId || '';
    const platformSection = () => {
      const isUpwork = inv.platform?.startsWith('upwork');
      let parts = [];

      // ── Upwork earnings box ──────────────────────────────────────────────
      if (isUpwork) {
        parts.push(`<div style="background:#e6f9e6;border-radius:10px;padding:16px;margin-top:18px;font-size:12px">
          <div style="font-weight:800;color:#14a800;margin-bottom:8px">🟢 ${inv.platform==='upwork_company'?'Upwork (Company)':'Upwork (Personal)'} Earnings</div>
          ${inv.upworkContractId ? `<div>Contract ID: <strong>${inv.upworkContractId}</strong></div>` : ''}
          ${inv.upworkJobTitle ? `<div>Job Title: ${inv.upworkJobTitle}</div>` : ''}
          ${inv.upworkFeePercent ? `<div style="margin-top:6px;color:#555">Service Fee (${inv.upworkFeePercent}%): <strong style="color:#ef4444">− ${fmt(inv.upworkFeeAmount)}</strong></div>
          <div style="font-weight:800;color:#14a800;font-size:14px;margin-top:4px">Net Payout: ${fmt(inv.upworkNetAmount||inv.total)}</div>` : ''}
        </div>`);
      }

      // ── PayPal ─────────────────────────────────────────────────────────
      if (inv.platform === 'paypal') {
        const paypalEmail = acc.paypalEmail || acc.email || '';
        parts.push(`<div style="background:#e8f0fe;border-radius:12px;padding:18px;margin-top:18px;font-size:12px;border:1.5px solid #bfdbfe">
          <div style="font-weight:800;color:#003087;margin-bottom:10px;font-size:13px">💳 PayPal Payment Details</div>
          ${paypalEmail ? `<div style="margin-bottom:4px">Send Payment to: <strong style="font-size:14px;color:#003087">${paypalEmail}</strong></div>` : ''}
          ${acc.owner ? `<div style="color:#64748b">Account Name: ${acc.owner}</div>` : ''}
          ${inv.platformRefId ? `<div style="margin-top:8px;padding:8px 10px;background:#dbeafe;border-radius:6px;color:#1e40af">Transaction ID: <strong>${inv.platformRefId}</strong></div>` : ''}
        </div>`);
      }

      // ── Wise ──────────────────────────────────────────────────────────
      if (inv.platform === 'wise') {
        const wiseEmail = acc.wiseEmail || acc.email || '';
        parts.push(`<div style="background:linear-gradient(135deg,#d4f5e0,#c6f13533);border-radius:12px;padding:18px;margin-top:18px;font-size:12px;border:1.5px solid #9fe870">
          <div style="font-weight:800;color:#163300;margin-bottom:10px;font-size:13px">💙 Wise Transfer Details</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
            ${wiseEmail ? `<div><div style="color:#4a7c1f;font-size:10px;font-weight:700;margin-bottom:3px">WISE EMAIL</div><div style="font-weight:800;color:#163300;font-size:13px">${wiseEmail}</div></div>` : ''}
            ${acc.owner ? `<div><div style="color:#4a7c1f;font-size:10px;font-weight:700;margin-bottom:3px">BENEFICIARY NAME</div><div style="font-weight:700;color:#163300">${acc.owner}</div></div>` : ''}
            ${acc.wiseCurrency ? `<div><div style="color:#4a7c1f;font-size:10px;font-weight:700;margin-bottom:3px">RECEIVE CURRENCY</div><div style="font-weight:700;color:#163300">${acc.wiseCurrency}</div></div>` : ''}
            ${inv.exchangeRate > 1 ? `<div><div style="color:#4a7c1f;font-size:10px;font-weight:700;margin-bottom:3px">EXCHANGE RATE</div><div style="font-weight:700;color:#163300">1 ${cur} = ₹${inv.exchangeRate}</div></div>` : ''}
          </div>
          ${inv.wiseTransferId ? `<div style="margin-top:10px;padding:8px 10px;background:#9fe87044;border-radius:6px;color:#163300">Transfer ID: <strong>${inv.wiseTransferId}</strong></div>` : ''}
          ${inv.wiseReceiveAmount ? `<div style="margin-top:8px;font-weight:900;color:#163300;font-size:15px">You Receive: ₹${inv.wiseReceiveAmount?.toLocaleString('en-IN')}</div>` : ''}
        </div>`);
      }

      // ── Bank / Local payments ─────────────────────────────────────────
      const showBank = acc.bankName || acc.accountNumber || acc.ifscCode || upiId;
      if (showBank) {
        const gstLine = (inv.platform==='local_gst' && (acc.gstNumber || co.gstNumber))
          ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700">GST NUMBER</div><div style="font-weight:700">${acc.gstNumber||co.gstNumber}</div></div>` : '';
        const upiLine = upiId
          ? `<div style="grid-column:1/-1;margin-top:10px;padding:10px 12px;background:#ede9fe;border-radius:8px;display:flex;align-items:center;gap:10px">
              <span style="font-size:18px">📱</span>
              <div>
                <div style="color:#94a3b8;font-size:10px;font-weight:700">UPI ID (instant transfer)</div>
                <div style="font-weight:800;font-size:14px;color:#5b21b6">${upiId}</div>
              </div>
            </div>` : '';
        parts.push(`<div style="background:#f8fafc;border-radius:12px;padding:18px;margin-top:18px;font-size:12px;border:1.5px solid #e2e8f0">
          <div style="font-weight:800;color:#0f172a;margin-bottom:4px;font-size:13px">🏦 ${acc.name||'Bank Details for Payment'}</div>
          <div style="color:#64748b;margin-bottom:12px">Beneficiary: <strong style="color:#0f172a">${acc.owner||acc.accountHolder||''}</strong>${acc.branchName ? ` · Branch: ${acc.branchName}` : ''}</div>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:12px">
            ${acc.bankName ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:3px">BANK NAME</div><div style="font-weight:700;color:#0f172a">${acc.bankName}</div></div>` : ''}
            ${acc.accountHolder||acc.owner ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:3px">ACCOUNT HOLDER</div><div style="font-weight:700;color:#0f172a">${acc.accountHolder||acc.owner}</div></div>` : ''}
            ${acc.accountNumber ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:3px">ACCOUNT NUMBER</div><div style="font-weight:700;color:#0f172a;font-family:monospace;letter-spacing:0.05em">${acc.accountNumber}</div></div>` : ''}
            ${acc.ifscCode ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:3px">IFSC CODE</div><div style="font-weight:700;color:#0f172a">${acc.ifscCode}</div></div>` : ''}
            ${gstLine}
          </div>
          ${upiLine}
        </div>`);
      }

      return parts.join('');
    };

    const clientInfo = inv.clientSnapshot || {};
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Invoice ${inv.invoiceNumber}</title>
<style>
* { box-sizing:border-box; margin:0; padding:0; }
body { font-family:'Segoe UI',Arial,sans-serif; font-size:13px; color:#1e293b; padding:44px; background:#fff; }
.hdr { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #0f172a; padding-bottom:24px; margin-bottom:28px; }
.co-tag { font-size:11px; color:#6366f1; margin:4px 0; }
.co-meta { font-size:11px; color:#64748b; line-height:1.7; margin-top:6px; }
.inv-badge { font-size:32px; font-weight:900; color:#0f172a; letter-spacing:-0.02em; }
.inv-num { font-size:13px; color:#64748b; margin-top:4px; }
.inv-status { display:inline-block; padding:4px 12px; border-radius:8px; font-size:11px; font-weight:800; margin-top:8px; background:${STATUS_CFG[inv.status]?.bg}; color:${STATUS_CFG[inv.status]?.color}; }
.parties { display:grid; grid-template-columns:1fr 1fr 1fr; gap:28px; background:#f8fafc; border-radius:12px; padding:20px; margin-bottom:24px; }
.party-lbl { font-size:10px; text-transform:uppercase; letter-spacing:.07em; color:#94a3b8; font-weight:700; margin-bottom:6px; }
.party-name { font-size:15px; font-weight:800; color:#0f172a; margin-bottom:3px; }
.party-sub { font-size:12px; color:#64748b; line-height:1.6; }
table.items { width:100%; border-collapse:collapse; margin-bottom:20px; }
table.items th { background:#f8fafc; padding:10px 12px; text-align:left; font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:.05em; border-bottom:2px solid #e2e8f0; }
table.items td { padding:10px 12px; border-bottom:1px solid #f1f5f9; font-size:13px; }
table.items tr.total-row td { font-weight:800; background:#f8fafc; }
.totals-box { float:right; width:300px; border:1.5px solid #e2e8f0; border-radius:12px; overflow:hidden; }
.totals-box table { width:100%; border-collapse:collapse; }
.totals-box td { padding:8px 14px; font-size:13px; }
.totals-box tr:nth-child(even) { background:#fafbfc; }
.totals-box .grand td { font-weight:900; font-size:15px; background:#0f172a; color:#fff; padding:12px 14px; }
.plat-pill { display:inline-block; background:${plat.bg}; color:${plat.color}; border-radius:8px; padding:3px 10px; font-size:11px; font-weight:700; margin-bottom:8px; }
.notes-box { clear:both; border-top:1px solid #e2e8f0; margin-top:24px; padding-top:16px; display:grid; grid-template-columns:1fr 1fr; gap:24px; }
.notes-lbl { font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; font-weight:700; margin-bottom:6px; }
.footer { margin-top:28px; text-align:center; font-size:11px; color:#94a3b8; border-top:1px dashed #e2e8f0; padding-top:16px; }
@media print { body { padding:24px; } }
</style></head><body>

<div class="hdr">
  <div>
    ${logoHtml}
    ${co.name ? `<div style="font-size:18px;font-weight:800;color:#0f172a;margin-top:6px">${co.name}</div>` : ''}
    ${co.tagline ? `<div class="co-tag">${co.tagline}</div>` : ''}
    <div class="co-meta">
      ${[co.address, co.city, co.country].filter(Boolean).join(', ')}<br/>
      ${[co.email, co.phone].filter(Boolean).join(' · ')}
      ${co.gstNumber ? `<br/>GST: ${co.gstNumber}` : ''}
      ${co.panNumber ? ` · PAN: ${co.panNumber}` : ''}
    </div>
  </div>
  <div style="text-align:right">
    <div class="plat-pill">${plat.icon} ${plat.label}</div>
    <div class="inv-badge">INVOICE</div>
    <div class="inv-num"># ${inv.invoiceNumber}</div>
    <div class="inv-num">Issue Date: ${FMTD(inv.issueDate)}</div>
    ${inv.dueDate ? `<div class="inv-num">Due Date: ${FMTD(inv.dueDate)}</div>` : ''}
    <div class="inv-status">${STATUS_CFG[inv.status]?.label || inv.status}</div>
    ${gstBadge}
  </div>
</div>

<div class="parties">
  <div>
    <div class="party-lbl">Bill To</div>
    <div class="party-name">${clientInfo.name || 'Client'}</div>
    <div class="party-sub">
      ${clientInfo.company ? clientInfo.company+'<br/>' : ''}
      ${clientInfo.email || ''}<br/>
      ${clientInfo.address ? clientInfo.address+'<br/>' : ''}
      ${[clientInfo.country].filter(Boolean).join('')}
      ${clientInfo.gstNumber ? `<br/>GST: ${clientInfo.gstNumber}` : ''}
    </div>
  </div>
  <div>
    <div class="party-lbl">Project</div>
    <div class="party-name">${inv.project?.name || inv.upworkJobTitle || 'General Services'}</div>
    ${inv.upworkContractId ? `<div class="party-sub">Contract: ${inv.upworkContractId}</div>` : ''}
    ${inv.platformRefId ? `<div class="party-sub">Ref: ${inv.platformRefId}</div>` : ''}
  </div>
  <div>
    <div class="party-lbl">Payment</div>
    <div class="party-name" style="font-size:13px">${plat.icon} ${plat.label}</div>
    <div class="party-sub">
      Currency: ${cur}<br/>
      ${inv.exchangeRate > 1 ? `Rate: 1 ${cur} = ₹${inv.exchangeRate}<br/>` : ''}
      ${inv.paidOn ? `Paid: ${FMTD(inv.paidOn)}` : inv.dueDate ? `Due: ${FMTD(inv.dueDate)}` : ''}
    </div>
  </div>
</div>

<table class="items">
  <thead><tr><th>#</th><th>Description</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Amount</th></tr></thead>
  <tbody>
    ${inv.lineItems?.map((item, i) => `
    <tr>
      <td style="color:#94a3b8">${i+1}</td>
      <td>${item.description}</td>
      <td>${item.quantity}</td>
      <td style="color:#94a3b8;font-size:11px">${item.unit||'hrs'}</td>
      <td>${fmt(item.rate)}</td>
      <td style="font-weight:600">${fmt(item.amount || item.quantity*item.rate)}</td>
    </tr>`).join('')}
  </tbody>
</table>

<div class="totals-box">
  <table>
    <tr><td>Subtotal</td><td style="text-align:right">${fmt(inv.subtotal||subtotal)}</td></tr>
    ${(inv.discountAmount||discountAmount) > 0 ? `<tr><td>Discount (${inv.discountPct}%)</td><td style="text-align:right;color:#ef4444">− ${fmt(inv.discountAmount||discountAmount)}</td></tr>` : ''}
    ${taxRows()}
    <tr class="grand"><td>TOTAL</td><td style="text-align:right">${fmt(inv.total||total)}</td></tr>
    ${inv.amountInINR && inv.currency !== 'INR' ? `<tr><td style="font-size:11px;color:#94a3b8">In INR (approx.)</td><td style="text-align:right;font-size:11px;color:#94a3b8">₹${(inv.amountInINR||0).toLocaleString('en-IN')}</td></tr>` : ''}
  </table>
</div>

${platformSection()}

<div class="notes-box">
  ${inv.notes ? `<div><div class="notes-lbl">Notes</div><div style="font-size:13px;color:#475569;line-height:1.7">${inv.notes}</div></div>` : '<div></div>'}
  ${inv.terms ? `<div><div class="notes-lbl">Terms & Conditions</div><div style="font-size:12px;color:#64748b;line-height:1.7">${inv.terms}</div></div>` : ''}
</div>

<div class="footer">
  Generated on ${new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})} · Thank you for your business!
</div>
</body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = invoices.filter(i => {
    if (filterPlat   && i.platform !== filterPlat)   return false;
    if (filterStatus && i.status   !== filterStatus) return false;
    if (filterCur    && i.currency !== filterCur)    return false;
    return true;
  });

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        @keyframes spin { to { transform:rotate(360deg) } }
        .inv-row:hover { background:#f8fafc!important; }
        .plat-btn:hover { transform:translateY(-2px); box-shadow:0 6px 20px #0002!important; }
        input:focus, select:focus, textarea:focus { border-color:#0f172a!important; background:#fff!important; }
      `}</style>

      {/* ──────────── LIST VIEW ─────────────────────────────────────────── */}
      {view === 'list' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:16 }}>
            <div>
              <h1 style={{ margin:0, fontSize:32, fontWeight:900, color:'#0f172a', letterSpacing:'-0.02em' }}>🧾 Invoices</h1>
              <p style={{ margin:'5px 0 0', color:'#94a3b8', fontSize:14 }}>Upwork · Wise · PayPal · Local GST · Bank Transfer</p>
            </div>
            <button style={S.btn('primary')} onClick={openNew}>+ New Invoice</button>
          </div>

          {/* KPI strip */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
            {[
              { l:'Total Invoiced',  v: `₹${(stats.totalINR||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`, sub:`${stats.total||0} invoices`,     c:'var(--pm-primary)' },
              { l:'Paid',            v: stats.paidAmount ? FMT(stats.paidAmount, invoices.find(i=>i.status==='paid')?.currency||'USD') : '₹0', sub:`${stats.paid||0} invoices`, c:'#22c55e' },
              { l:'Outstanding',     v: `${stats.pending||0} invoices`, sub:'sent / viewed',     c:'#3b82f6' },
              { l:'Overdue',         v: stats.overdue||0, sub:'past due date',   c:'#ef4444' },
              { l:'Drafts',          v: stats.draft||0,   sub:'not sent yet',    c:'#f59e0b' },
            ].map(k => (
              <div key={k.l} style={{ ...S.card, padding:'18px 20px', borderLeft:`4px solid ${k.c}` }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>{k.l}</div>
                <div style={{ fontSize:22, fontWeight:900, color:'#0f172a' }}>{k.v}</div>
                <div style={{ fontSize:11, color:k.c, fontWeight:600, marginTop:4 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Platform breakdown */}
          {stats.byPlatform && Object.keys(stats.byPlatform).length > 0 && (
            <div style={{ ...S.card, padding:'18px 22px', marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:14 }}>Revenue by Platform</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {Object.entries(stats.byPlatform).map(([plat, amt]) => {
                  const cfg = PLATFORMS[plat] || PLATFORMS.other;
                  const pInvoices = invoices.filter(i=>i.platform===plat);
                  const currency  = pInvoices[0]?.currency || 'USD';
                  return (
                    <div key={plat} className="plat-btn" onClick={() => setFilterPlat(filterPlat===plat?'':plat)}
                      style={{ padding:'10px 16px', borderRadius:12, background: filterPlat===plat ? cfg.color : cfg.bg,
                        color: filterPlat===plat ? '#fff' : cfg.color, cursor:'pointer', transition:'all .2s', boxShadow:'0 2px 8px #0001',
                        border:`1.5px solid ${filterPlat===plat ? cfg.color : 'transparent'}` }}>
                      <div style={{ fontSize:14, fontWeight:800 }}>{cfg.icon} {cfg.label}</div>
                      <div style={{ fontSize:12, opacity:.8, marginTop:3 }}>{FMT(amt, currency)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
            <select style={{ ...INP({ width:'auto', padding:'8px 13px' }) }} value={filterPlat} onChange={e=>setFilterPlat(e.target.value)}>
              <option value="">All Platforms</option>
              {Object.entries(PLATFORMS).map(([v,p])=><option key={v} value={v}>{p.icon} {p.label}</option>)}
            </select>
            <select style={{ ...INP({ width:'auto', padding:'8px 13px' }) }} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              {Object.entries(STATUS_CFG).map(([v,s])=><option key={v} value={v}>{s.label}</option>)}
            </select>
            <select style={{ ...INP({ width:'auto', padding:'8px 13px' }) }} value={filterCur} onChange={e=>setFilterCur(e.target.value)}>
              <option value="">All Currencies</option>
              {CURRENCIES.map(c=><option key={c}>{c}</option>)}
            </select>
            {(filterPlat||filterStatus||filterCur) && <button style={S.btn()} onClick={()=>{setFilterPlat('');setFilterStatus('');setFilterCur('');}}>✕ Clear</button>}
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ textAlign:'center', padding:80 }}>
              <div style={{ width:36, height:36, border:'3px solid #f1f5f9', borderTop:'3px solid #0f172a', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }}/>
            </div>
          ) : (
            <div style={S.card}>
              {filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:'60px 20px', color:'#94a3b8' }}>
                  <div style={{ fontSize:52, marginBottom:14 }}>🧾</div>
                  <div style={{ fontWeight:700, fontSize:17, color:'#475569', marginBottom:8 }}>No invoices yet</div>
                  <div style={{ fontSize:13, marginBottom:20 }}>Create your first invoice for Upwork, Wise, PayPal or local clients</div>
                  <button style={S.btn('primary')} onClick={openNew}>+ Create Invoice</button>
                </div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:'#f8fafc' }}>
                        {['Invoice #','Platform','Client','Project','Amount','Tax','Issue Date','Due Date','Status',''].map(h=>(
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((inv, i) => {
                        const plat   = PLATFORMS[inv.platform] || PLATFORMS.other;
                        const stCfg  = STATUS_CFG[inv.status]  || STATUS_CFG.draft;
                        const isOD   = inv.status !== 'paid' && inv.dueDate && new Date(inv.dueDate) < new Date();
                        return (
                          <tr key={inv._id} className="inv-row" style={{ background:i%2===0?'#fff':'#fafbfc', transition:'background .15s', cursor:'pointer' }}
                            onClick={() => { setSelected(inv); setView('detail'); }}>
                            <td style={{ ...S.td, fontWeight:800, color:'#0f172a', fontFamily:'monospace', fontSize:12 }}>{inv.invoiceNumber}</td>
                            <td style={S.td}>
                              <span style={{ padding:'3px 10px', borderRadius:7, fontSize:11, fontWeight:700, background:plat.bg, color:plat.color }}>{plat.icon} {plat.label}</span>
                            </td>
                            <td style={{ ...S.td, fontWeight:600 }}>
                              <div>{inv.clientSnapshot?.name || inv.client?.name || '—'}</div>
                              <div style={{ fontSize:11, color:'#94a3b8' }}>{inv.clientSnapshot?.country || ''}</div>
                            </td>
                            <td style={{ ...S.td, fontSize:12 }}>{inv.project?.name || inv.upworkJobTitle || '—'}</td>
                            <td style={{ ...S.td, fontWeight:800, color:'#0f172a' }}>
                              {FMT(inv.total, inv.currency)}
                              {inv.amountInINR && inv.currency !== 'INR' && <div style={{ fontSize:10, color:'#94a3b8' }}>≈ ₹{inv.amountInINR?.toLocaleString('en-IN',{maximumFractionDigits:0})}</div>}
                            </td>
                            <td style={{ ...S.td, fontSize:12 }}>
                              {inv.taxType === 'none' ? <span style={{ color:'#94a3b8' }}>—</span> : (
                                <span style={{ color:'#ff6b35', fontWeight:700 }}>{inv.taxType==='gst'?`GST ${inv.taxRate}%`:inv.taxType==='igst'?`IGST ${inv.taxRate}%`:`${inv.taxRate}%`}</span>
                              )}
                            </td>
                            <td style={{ ...S.td, fontSize:12, fontFamily:'monospace' }}>{FMTD(inv.issueDate)}</td>
                            <td style={{ ...S.td, fontSize:12, color: isOD ? '#ef4444' : '#475569', fontWeight: isOD ? 700 : 400 }}>
                              {FMTD(inv.dueDate)}{isOD && ' ⚠️'}
                            </td>
                            <td style={S.td}>
                              <span style={{ padding:'3px 10px', borderRadius:7, fontSize:11, fontWeight:700, background:stCfg.bg, color:stCfg.color }}>{stCfg.label}</span>
                            </td>
                            <td style={S.td} onClick={e=>e.stopPropagation()}>
                              <div style={{ display:'flex', gap:6 }}>
                                <button style={{ ...S.btn(), padding:'5px 10px', fontSize:11 }} onClick={()=>printInvoice(inv)}>🖨</button>
                                <button style={{ ...S.btn(), padding:'5px 10px', fontSize:11 }} onClick={()=>openEdit(inv)}>✏️</button>
                                {inv.status !== 'paid' && <button style={{ ...S.btn('success'), padding:'5px 10px', fontSize:11 }} onClick={()=>{setPayModal(inv._id);setPayForm({status:'paid',paymentMethod:'',paymentNotes:'',paidOn:new Date().toISOString().slice(0,10)});}}>💰 Pay</button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ──────────── FORM VIEW ─────────────────────────────────────────── */}
      {view === 'form' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Form header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:12 }}>
            <div>
              <button style={{ ...S.btn(), marginBottom:8, fontSize:12 }} onClick={()=>setView('list')}>← Back</button>
              <h2 style={{ margin:0, fontSize:24, fontWeight:900, color:'#0f172a' }}>{editId ? `Edit Invoice ${form.invoiceNumber}` : 'New Invoice'}</h2>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button style={S.btn('ghost')} onClick={()=>save('draft')}>💾 Save Draft</button>
              <button style={S.btn('primary')} onClick={()=>save('sent')}>📤 Save & Mark Sent</button>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:20, alignItems:'start' }}>

            {/* Left column */}
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

              {/* Platform picker */}
              <div style={{ ...S.card, padding:22 }}>
                <div style={S.section}>Payment Platform</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:8 }}>
                  {Object.entries(PLATFORMS).map(([k,p]) => (
                    <button key={k} type="button" onClick={() => handlePlatformChange(k)}
                      style={{ padding:'12px 10px', borderRadius:11, border:`2px solid ${form.platform===k ? p.color : '#e2e8f0'}`,
                        background: form.platform===k ? p.bg : '#fafbfc', cursor:'pointer', textAlign:'left', transition:'all .2s' }}>
                      <div style={{ fontSize:18, marginBottom:4 }}>{p.icon}</div>
                      <div style={{ fontSize:12, fontWeight:800, color: form.platform===k ? p.color : '#0f172a' }}>{p.label}</div>
                      <div style={{ fontSize:10, color:'#94a3b8', marginTop:2 }}>{p.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Invoice meta */}
              <div style={{ ...S.card, padding:22 }}>
                <div style={S.section}>Invoice Details</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                  <div><label style={S.label}>Invoice Number *</label><input style={INP()} value={form.invoiceNumber} onChange={e=>setForm(f=>({...f,invoiceNumber:e.target.value}))}/></div>
                  <div><label style={S.label}>Issue Date</label><input type="date" style={INP()} value={form.issueDate} onChange={e=>setForm(f=>({...f,issueDate:e.target.value}))}/></div>
                  <div><label style={S.label}>Due Date</label><input type="date" style={INP()} value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))}/></div>
                  <div>
                    <label style={S.label}>Currency</label>
                    <select style={INP()} value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}>
                      {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={S.label}>
                      Payment Account (where client pays) 
                      {form.platform?.startsWith('upwork') && <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:6, padding:'1px 8px', fontSize:10, fontWeight:700, marginLeft:8 }}>SELECT PER CONTRACT</span>}
                      {!form.platform?.startsWith('upwork') && form.paymentAccountId && <span style={{ background:'#dcfce7', color:'#14532d', borderRadius:6, padding:'1px 8px', fontSize:10, fontWeight:700, marginLeft:8 }}>AUTO-SELECTED</span>}
                    </label>
                    {/* Upwork: show card-style chooser so it's explicit and hard to miss */}
                    {form.platform?.startsWith('upwork') ? (
                      <div>
                        <div style={{ fontSize:12, color:'#92400e', background:'#fef3c7', borderRadius:8, padding:'8px 12px', marginBottom:10, border:'1px solid #fde68a' }}>
                          ⚠️ Upwork payment varies per contract — choose which account receives this payment:
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:8 }}>
                          {payAccounts.map(a => {
                            const active = form.paymentAccountId === a._id;
                            const ownerColor = { self:'var(--pm-primary)', spouse:'#ec4899', company:'#0f766e', other:'#64748b' }[a.ownerType] || '#64748b';
                            const ownerBg    = { self:'#ede9fe', spouse:'#fce7f3', company:'#ccfbf1', other:'#f1f5f9' }[a.ownerType] || '#f1f5f9';
                            return (
                              <button key={a._id} type="button" onClick={() => setForm(f => ({ ...f, paymentAccountId: a._id, paymentAccountSnapshot: buildAccSnapshot(a) }))}
                                style={{ padding:'12px 14px', borderRadius:12, border:`2px solid ${active ? ownerColor : '#e2e8f0'}`, background: active ? ownerBg : '#fafbfc',
                                  cursor:'pointer', textAlign:'left', transition:'all .15s', position:'relative' }}>
                                {active && <span style={{ position:'absolute', top:8, right:10, fontSize:14, color:ownerColor }}>✓</span>}
                                <div style={{ fontWeight:800, fontSize:13, color: active ? ownerColor : '#0f172a', marginBottom:3 }}>{a.name}</div>
                                <div style={{ fontSize:11, color:'#64748b' }}>{a.owner}</div>
                                {a.bankName && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{a.bankName}</div>}
                                {a.paypalEmail && <div style={{ fontSize:11, color:'#003087', marginTop:2 }}>💳 {a.paypalEmail}</div>}
                                {a.wiseEmail && <div style={{ fontSize:11, color:'#163300', marginTop:2 }}>💙 {a.wiseEmail}</div>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <select style={INP()} value={form.paymentAccountId||''} onChange={e => {
                          const acc = payAccounts.find(a=>a._id===e.target.value);
                          setForm(f => ({ ...f, paymentAccountId: e.target.value, paymentAccountSnapshot: acc ? buildAccSnapshot(acc) : {} }));
                        }}>
                          <option value="">— Select account —</option>
                          {payAccounts.map(a => <option key={a._id} value={a._id}>{a.name} ({a.owner})</option>)}
                        </select>
                        {form.paymentAccountId && (() => {
                          const acc = payAccounts.find(a=>a._id===form.paymentAccountId);
                          if (!acc) return null;
                          const ownerColor = { self:'var(--pm-primary)', spouse:'#ec4899', company:'#0f766e', other:'#64748b' }[acc.ownerType] || 'var(--pm-primary)';
                          return <div style={{ marginTop:8, padding:'10px 14px', borderRadius:9, background:'#f8fafc', border:`1.5px solid ${ownerColor}33`, fontSize:12, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
                            <span style={{ fontWeight:700, color:ownerColor }}>✓ {acc.name}</span>
                            {acc.bankName && <span style={{ color:'#475569' }}>🏦 {acc.bankName}</span>}
                            {acc.accountNumber && <span style={{ color:'#64748b', fontFamily:'monospace' }}>A/C: {acc.accountNumber}</span>}
                            {acc.ifscCode && <span style={{ color:'#94a3b8' }}>IFSC: {acc.ifscCode}</span>}
                            {acc.upiId && <span style={{ color:'var(--pm-primary)' }}>📱 {acc.upiId}</span>}
                            {acc.paypalEmail && <span style={{ color:'#003087' }}>💳 {acc.paypalEmail}</span>}
                            {acc.wiseEmail && <span style={{ color:'#163300' }}>💙 {acc.wiseEmail}</span>}
                          </div>;
                        })()}
                      </div>
                    )}
                  </div>
                  {form.currency !== 'INR' && <div><label style={S.label}>Exchange Rate (1 {form.currency} = ₹)</label><input type="number" style={INP()} value={form.exchangeRate} onChange={e=>setForm(f=>({...f,exchangeRate:parseFloat(e.target.value)||1}))}/></div>}
                  <div>
                    <label style={S.label}>Status</label>
                    <select style={INP()} value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                      {Object.entries(STATUS_CFG).map(([v,s])=><option key={v} value={v}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Client */}
              <div style={{ ...S.card, padding:22 }}>
                <div style={S.section}>Client Information</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={S.label}>Existing Client (optional)</label>
                    <select style={INP()} value={String(form.client||'')} onChange={e => {
                      const val = e.target.value;
                      const c = clients.find(x=>String(x._id)===String(val) || String(x.id)===String(val));
                      if (c) {
                        setForm(f => ({
                          ...f,
                          client: e.target.value,
                          project: '',
                          clientSnapshot: {
                            name:      c.name        || '',
                            email:     c.email       || '',
                            company:   c.company     || '',
                            address:   c.address     || '',
                            country:   c.country     || '',
                            gstNumber: c.gstNumber   || '',
                            phone:     c.phone       || '',
                          }
                        }));
                      } else {
                        setForm(f => ({ ...f, client: '', project: '', clientSnapshot: { name:'', email:'', company:'', address:'', country:'', gstNumber:'', phone:'' } }));
                      }
                    }}>
                      <option value="">— Select or enter manually below —</option>
                      {clients.map(c=><option key={c._id||c.id} value={String(c._id||c.id)}>{c.name} ({c.email})</option>)}
                    </select>
                  </div>
                  <div><label style={S.label}>Client Name *</label><input style={INP()} value={form.clientSnapshot.name} onChange={e=>setForm(f=>({...f,clientSnapshot:{...f.clientSnapshot,name:e.target.value}}))} placeholder="John Smith"/></div>
                  <div><label style={S.label}>Email</label><input style={INP()} value={form.clientSnapshot.email} onChange={e=>setForm(f=>({...f,clientSnapshot:{...f.clientSnapshot,email:e.target.value}}))} placeholder="john@company.com"/></div>
                  <div><label style={S.label}>Company</label><input style={INP()} value={form.clientSnapshot.company} onChange={e=>setForm(f=>({...f,clientSnapshot:{...f.clientSnapshot,company:e.target.value}}))} placeholder="Company Inc."/></div>
                  <div><label style={S.label}>Country</label><input style={INP()} value={form.clientSnapshot.country} onChange={e=>setForm(f=>({...f,clientSnapshot:{...f.clientSnapshot,country:e.target.value}}))} placeholder="USA / India / UK"/></div>
                  {['local_gst'].includes(form.platform) && (
                    <div><label style={S.label}>Client GST Number</label><input style={INP()} value={form.clientSnapshot.gstNumber} onChange={e=>setForm(f=>({...f,clientSnapshot:{...f.clientSnapshot,gstNumber:e.target.value}}))} placeholder="22AAAAA0000A1Z5"/></div>
                  )}
                  <div><label style={S.label}>Address</label><input style={INP()} value={form.clientSnapshot.address} onChange={e=>setForm(f=>({...f,clientSnapshot:{...f.clientSnapshot,address:e.target.value}}))} placeholder="123 Main St, City"/></div>
                </div>
              </div>

              {/* Project link */}
              <div style={{ ...S.card, padding:22 }}>
                <div style={S.section}>Project & Platform Details</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={S.label}>Link to Project</label>
                    <select style={INP()} value={form.project} onChange={e=>setForm(f=>({...f,project:e.target.value}))}>
                      <option value="">— None —</option>
                      {(form.client
                        ? projects.filter(p => String(p.clientId||p.client||'') === String(form.client))
                        : projects
                      ).map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                  </div>
                  {form.platform === 'upwork' && <>
                    <div><label style={S.label}>Upwork Job Title</label><input style={INP()} value={form.upworkJobTitle} onChange={e=>setForm(f=>({...f,upworkJobTitle:e.target.value}))} placeholder="React Developer – E-commerce App"/></div>
                    <div><label style={S.label}>Contract ID</label><input style={INP()} value={form.upworkContractId} onChange={e=>setForm(f=>({...f,upworkContractId:e.target.value}))} placeholder="~01234abcdef"/></div>
                    <div>
                      <label style={S.label}>Upwork Service Fee</label>
                      <select style={INP()} value={form.upworkFeePercent} onChange={e=>setForm(f=>({...f,upworkFeePercent:parseInt(e.target.value)}))}>
                        {UPWORK_FEES.map(f=><option key={f.v} value={f.v}>{f.l}</option>)}
                      </select>
                    </div>
                  </>}
                  {form.platform === 'wise' && <>
                    <div><label style={S.label}>Wise Transfer ID</label><input style={INP()} value={form.wiseTransferId} onChange={e=>setForm(f=>({...f,wiseTransferId:e.target.value}))} placeholder="P123456789"/></div>
                    <div><label style={S.label}>Amount to Receive (₹)</label><input type="number" style={INP()} value={form.wiseReceiveAmount||''} onChange={e=>setForm(f=>({...f,wiseReceiveAmount:parseFloat(e.target.value)||0}))} placeholder="e.g. 83500"/></div>
                  </>}
                  {form.platform === 'paypal' && (
                    <div><label style={S.label}>PayPal Transaction ID</label><input style={INP()} value={form.platformRefId} onChange={e=>setForm(f=>({...f,platformRefId:e.target.value}))} placeholder="9XJ12345AB123456C"/></div>
                  )}
                  {['local_gst','local_nogst','bank_transfer'].includes(form.platform) && (
                    <div><label style={S.label}>Reference / PO Number</label><input style={INP()} value={form.platformRefId} onChange={e=>setForm(f=>({...f,platformRefId:e.target.value}))} placeholder="PO-2025-001"/></div>
                  )}
                </div>
              </div>

              {/* Line items */}
              <div style={{ ...S.card, padding:22 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <div style={S.section}>Line Items</div>
                  <button type="button" style={{ ...S.btn(), padding:'6px 14px', fontSize:12 }} onClick={addLine}>+ Add Row</button>
                </div>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#f8fafc' }}>
                      <th style={{ ...S.th, width:'40%' }}>Description</th>
                      <th style={{ ...S.th, width:'10%' }}>Qty</th>
                      <th style={{ ...S.th, width:'12%' }}>Unit</th>
                      <th style={{ ...S.th, width:'17%' }}>Rate ({form.currency})</th>
                      <th style={{ ...S.th, width:'17%' }}>Amount</th>
                      <th style={{ ...S.th, width:'4%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lineItems.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom:'1px solid #f1f5f9' }}>
                        <td style={{ padding:'6px 4px' }}><input style={INP({ margin:0 })} value={item.description} onChange={e=>setLine(idx,'description',e.target.value)} placeholder="e.g. React development, Design, Consultation"/></td>
                        <td style={{ padding:'6px 4px' }}><input type="number" style={INP({ margin:0 })} value={item.quantity} onChange={e=>setLine(idx,'quantity',e.target.value)} min="0" step="0.5"/></td>
                        <td style={{ padding:'6px 4px' }}>
                          <select style={INP({ margin:0 })} value={item.unit} onChange={e=>setLine(idx,'unit',e.target.value)}>
                            {['hrs','days','units','pages','fixed','%'].map(u=><option key={u}>{u}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:'6px 4px' }}><input type="number" style={INP({ margin:0 })} value={item.rate} onChange={e=>setLine(idx,'rate',e.target.value)} min="0" step="0.01"/></td>
                        <td style={{ padding:'6px 4px', fontWeight:700, color:'#0f172a', paddingLeft:12 }}>{FMT(item.quantity * item.rate, form.currency)}</td>
                        <td style={{ padding:'6px 4px' }}>{form.lineItems.length > 1 && <button type="button" onClick={()=>removeLine(idx)} style={{ ...S.btn('danger'), padding:'5px 8px', fontSize:12 }}>×</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Notes & Terms */}
              <div style={{ ...S.card, padding:22 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  <div>
                    <label style={S.label}>Notes to Client</label>
                    <textarea style={{ ...INP(), height:90, resize:'vertical' }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Thank you for your business!"/>
                  </div>
                  <div>
                    <label style={S.label}>Terms & Conditions</label>
                    <textarea style={{ ...INP(), height:90, resize:'vertical' }} value={form.terms} onChange={e=>setForm(f=>({...f,terms:e.target.value}))} placeholder="Payment due within 30 days."/>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column — Totals calculator */}
            <div style={{ position:'sticky', top:24 }}>
              <div style={{ ...S.card, padding:22, marginBottom:16 }}>
                <div style={S.section}>Totals</div>
                {[['Subtotal', subtotal]].map(([l,v]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f8fafc', fontSize:14 }}>
                    <span style={{ color:'#64748b' }}>{l}</span><span style={{ fontWeight:600 }}>{FMT(v, form.currency)}</span>
                  </div>
                ))}
                {/* Discount */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f8fafc' }}>
                  <span style={{ color:'#64748b', fontSize:13 }}>Discount</span>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input type="number" style={{ ...INP({ width:70, margin:0, padding:'5px 8px', fontSize:12 }), textAlign:'right' }} value={form.discountPct} onChange={e=>setForm(f=>({...f,discountPct:parseFloat(e.target.value)||0}))} min="0" max="100"/>
                    <span style={{ fontSize:12, color:'#94a3b8' }}>%</span>
                    {discountAmount > 0 && <span style={{ fontSize:13, fontWeight:600, color:'#ef4444' }}>−{FMT(discountAmount, form.currency)}</span>}
                  </div>
                </div>
                {/* Tax */}
                <div style={{ padding:'8px 0', borderBottom:'1px solid #f8fafc' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <span style={{ color:'#64748b', fontSize:13 }}>Tax</span>
                    <select style={{ ...INP({ width:'auto', padding:'5px 10px', margin:0, fontSize:12 }) }} value={form.taxType} onChange={e=>setForm(f=>({...f,taxType:e.target.value}))}>
                      {TAX_TYPES.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </div>
                  {form.taxType !== 'none' && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'flex-end' }}>
                      <input type="number" style={{ ...INP({ width:70, margin:0, padding:'5px 8px', fontSize:12 }), textAlign:'right' }} value={form.taxRate} onChange={e=>setForm(f=>({...f,taxRate:parseFloat(e.target.value)||0}))} min="0" max="100"/>
                      <span style={{ fontSize:12, color:'#94a3b8' }}>%</span>
                    </div>
                  )}
                  {cgst > 0 && <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, fontSize:12, color:'#ff6b35' }}><span>CGST ({form.taxRate/2}%)</span><span>{FMT(cgst, form.currency)}</span></div>}
                  {sgst > 0 && <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#ff6b35' }}><span>SGST ({form.taxRate/2}%)</span><span>{FMT(sgst, form.currency)}</span></div>}
                  {igst > 0 && <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#ff6b35' }}><span>IGST ({form.taxRate}%)</span><span>{FMT(igst, form.currency)}</span></div>}
                  {otherTax > 0 && <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#ff6b35' }}><span>Tax ({form.taxRate}%)</span><span>{FMT(otherTax, form.currency)}</span></div>}
                </div>
                {/* Grand total */}
                <div style={{ background:'#0f172a', borderRadius:12, padding:'16px 18px', marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:600 }}>TOTAL</div>
                  <div style={{ color:'#fff', fontSize:24, fontWeight:900 }}>{FMT(total, form.currency)}</div>
                </div>
                {form.currency !== 'INR' && form.exchangeRate > 1 && (
                  <div style={{ textAlign:'right', fontSize:11, color:'#94a3b8', marginTop:8 }}>≈ ₹{(total * form.exchangeRate).toLocaleString('en-IN',{maximumFractionDigits:0})} INR</div>
                )}
                {form.platform === 'upwork' && upworkFee > 0 && (
                  <div style={{ background:'#e6f9e6', borderRadius:10, padding:'12px 14px', marginTop:12 }}>
                    <div style={{ fontSize:11, color:'#14a800', fontWeight:700, marginBottom:6 }}>🟢 After Upwork Fee ({form.upworkFeePercent}%)</div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}><span style={{ color:'#555' }}>Service Fee</span><span style={{ color:'#ef4444' }}>−{FMT(upworkFee, form.currency)}</span></div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, fontSize:15, marginTop:6 }}><span>You Receive</span><span style={{ color:'#14a800' }}>{FMT(upworkNet, form.currency)}</span></div>
                  </div>
                )}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <button style={{ ...S.btn('primary'), justifyContent:'center', padding:'13px 0' }} onClick={()=>save('sent')}>📤 Save & Mark Sent</button>
                <button style={{ ...S.btn('ghost'), justifyContent:'center' }} onClick={()=>save('draft')}>💾 Save as Draft</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ──────────── DETAIL VIEW ───────────────────────────────────────── */}
      {view === 'detail' && selected && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
            <button style={{ ...S.btn(), fontSize:12 }} onClick={()=>setView('list')}>← Back to Invoices</button>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button style={S.btn()} onClick={()=>printInvoice(selected)}>🖨 Print / PDF</button>
              <button style={S.btn()} onClick={()=>openEdit(selected)}>✏️ Edit</button>
              {selected.status !== 'paid' && <button style={S.btn('success')} onClick={()=>{setPayModal(selected._id);setPayForm({status:'paid',paymentMethod:'',paymentNotes:'',paidOn:new Date().toISOString().slice(0,10)});}}>💰 Record Payment</button>}
              {selected.platform === 'razorpay' && !['paid','cancelled'].includes(selected.status) && (
                <button style={{ ...S.btn(), background:'linear-gradient(135deg,#2d6be4,#4f8ef7)', color:'#fff', opacity: rzpProcessing ? 0.7 : 1 }} disabled={rzpProcessing} onClick={()=>payViaRazorpay(selected)}>
                  💎 {rzpProcessing ? 'Processing...' : 'Pay via Razorpay'}
                </button>
              )}
              <button style={S.btn('danger')} onClick={()=>deleteInvoice(selected._id)}>🗑 Delete</button>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:20 }}>
            <div style={{ ...S.card, padding:28 }}>
              {/* Invoice header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, paddingBottom:20, borderBottom:'2px solid #f1f5f9' }}>
                <div>
                  {company.logoBase64 ? <img src={company.logoBase64} alt="logo" style={{ maxHeight:56, marginBottom:8, objectFit:'contain' }}/> : null}
                  <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>{company.name || 'Your Company'}</div>
                  {company.tagline && <div style={{ fontSize:12, color:'var(--pm-primary)', marginTop:2 }}>{company.tagline}</div>}
                  <div style={{ fontSize:12, color:'#94a3b8', marginTop:4, lineHeight:1.6 }}>
                    {[company.address, company.city, company.country].filter(Boolean).join(', ')}<br/>
                    {company.gstNumber && `GST: ${company.gstNumber}`}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ padding:'3px 12px', borderRadius:8, fontSize:12, fontWeight:700, display:'inline-block', background:PLATFORMS[selected.platform]?.bg, color:PLATFORMS[selected.platform]?.color, marginBottom:8 }}>
                    {PLATFORMS[selected.platform]?.icon} {PLATFORMS[selected.platform]?.label}
                  </div>
                  <div style={{ fontSize:28, fontWeight:900, color:'#0f172a' }}>INVOICE</div>
                  <div style={{ fontFamily:'monospace', fontSize:14, color:'#64748b', marginTop:4 }}>#{selected.invoiceNumber}</div>
                  <span style={{ padding:'4px 12px', borderRadius:8, fontSize:12, fontWeight:700, display:'inline-block', marginTop:8, background:STATUS_CFG[selected.status]?.bg, color:STATUS_CFG[selected.status]?.color }}>
                    {STATUS_CFG[selected.status]?.label}
                  </span>
                </div>
              </div>

              {/* Bill to / dates */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, marginBottom:24, background:'#f8fafc', borderRadius:12, padding:18 }}>
                <div>
                  <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Bill To</div>
                  <div style={{ fontWeight:800, fontSize:15 }}>{selected.clientSnapshot?.name || '—'}</div>
                  <div style={{ fontSize:12, color:'#64748b', lineHeight:1.6, marginTop:4 }}>
                    {selected.clientSnapshot?.company && <div>{selected.clientSnapshot.company}</div>}
                    {selected.clientSnapshot?.email}
                    {selected.clientSnapshot?.gstNumber && <div style={{ marginTop:4 }}>GST: {selected.clientSnapshot.gstNumber}</div>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Dates</div>
                  <div style={{ fontSize:13, lineHeight:2 }}>
                    <div>Issue: <strong>{FMTD(selected.issueDate)}</strong></div>
                    <div>Due: <strong style={{ color: selected.status!=='paid' && selected.dueDate && new Date(selected.dueDate)<new Date() ? '#ef4444' : '#0f172a' }}>{FMTD(selected.dueDate)}</strong></div>
                    {selected.paidOn && <div>Paid: <strong style={{ color:'#22c55e' }}>{FMTD(selected.paidOn)}</strong></div>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Payment</div>
                  <div style={{ fontSize:13, lineHeight:2 }}>
                    <div>Currency: <strong>{selected.currency}</strong></div>
                    {selected.exchangeRate > 1 && <div>Rate: <strong>1 {selected.currency} = ₹{selected.exchangeRate}</strong></div>}
                    {selected.paymentMethod && <div>Method: <strong>{selected.paymentMethod}</strong></div>}
                  </div>
                </div>
              </div>

              {/* Line items table */}
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:20 }}>
                <thead>
                  <tr style={{ background:'#f8fafc' }}>
                    {['#','Description','Qty','Unit','Rate','Amount'].map(h=><th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {selected.lineItems?.map((item, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                      <td style={{ ...S.td, color:'#94a3b8', width:30 }}>{i+1}</td>
                      <td style={{ ...S.td, fontWeight:600, color:'#0f172a' }}>{item.description}</td>
                      <td style={S.td}>{item.quantity}</td>
                      <td style={{ ...S.td, fontSize:11, color:'#94a3b8' }}>{item.unit||'hrs'}</td>
                      <td style={S.td}>{FMT(item.rate, selected.currency)}</td>
                      <td style={{ ...S.td, fontWeight:700 }}>{FMT(item.amount||item.quantity*item.rate, selected.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <div style={{ width:300 }}>
                  {[
                    ['Subtotal', selected.subtotal, false],
                    selected.discountAmount > 0 && [`Discount (${selected.discountPct}%)`, -selected.discountAmount, 'red'],
                    selected.cgst > 0 && [`CGST (${selected.taxRate/2}%)`, selected.cgst, 'orange'],
                    selected.sgst > 0 && [`SGST (${selected.taxRate/2}%)`, selected.sgst, 'orange'],
                    selected.igst > 0 && [`IGST (${selected.taxRate}%)`, selected.igst, 'orange'],
                    selected.otherTax > 0 && [`Tax (${selected.taxRate}%)`, selected.otherTax, 'orange'],
                  ].filter(Boolean).map(([l,v,c]) => (
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 14px', fontSize:13, borderBottom:'1px solid #f8fafc' }}>
                      <span style={{ color:'#64748b' }}>{l}</span>
                      <span style={{ fontWeight:600, color:c==='red'?'#ef4444':c==='orange'?'#ff6b35':'#0f172a' }}>{c==='red'?'−':''}{FMT(Math.abs(v), selected.currency)}</span>
                    </div>
                  ))}
                  <div style={{ background:'#0f172a', borderRadius:10, padding:'14px 18px', marginTop:6, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:600 }}>TOTAL</span>
                    <span style={{ color:'#fff', fontSize:22, fontWeight:900 }}>{FMT(selected.total, selected.currency)}</span>
                  </div>
                  {selected.amountInINR && selected.currency !== 'INR' && (
                    <div style={{ textAlign:'right', fontSize:11, color:'#94a3b8', marginTop:6 }}>≈ ₹{selected.amountInINR?.toLocaleString('en-IN',{maximumFractionDigits:0})} INR</div>
                  )}
                  {selected.platform === 'upwork' && selected.upworkFeeAmount > 0 && (
                    <div style={{ background:'#e6f9e6', borderRadius:10, padding:'12px 14px', marginTop:10 }}>
                      <div style={{ fontSize:11, color:'#14a800', fontWeight:800, marginBottom:4 }}>After Upwork Fee ({selected.upworkFeePercent}%)</div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}><span>Fee</span><span style={{ color:'#ef4444' }}>−{FMT(selected.upworkFeeAmount, selected.currency)}</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, fontSize:14, color:'#14a800', marginTop:4 }}><span>You Receive</span><span>{FMT(selected.upworkNetAmount, selected.currency)}</span></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {(selected.notes || selected.terms) && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginTop:24, paddingTop:20, borderTop:'1px solid #f1f5f9' }}>
                  {selected.notes && <div><div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Notes</div><div style={{ fontSize:13, color:'#475569', lineHeight:1.7 }}>{selected.notes}</div></div>}
                  {selected.terms && <div><div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Terms</div><div style={{ fontSize:12, color:'#64748b', lineHeight:1.7 }}>{selected.terms}</div></div>}
                </div>
              )}
            </div>

            {/* Right sidebar */}
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {/* Quick status change */}
              <div style={{ ...S.card, padding:18 }}>
                <div style={S.section}>Update Status</div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {Object.entries(STATUS_CFG).map(([v,s]) => (
                    <button key={v} style={{ padding:'9px 14px', borderRadius:9, border:`1.5px solid ${selected.status===v ? s.color : '#e2e8f0'}`, background:selected.status===v?s.bg:'#fafbfc', color:selected.status===v?s.color:'#64748b', cursor:'pointer', fontWeight:700, fontSize:13, textAlign:'left', transition:'all .15s' }}
                      onClick={async () => {
                        if (v === 'paid' && selected.status !== 'paid') {
                          // Open full payment modal for proper tracking
                          setPayModal(selected._id);
                          setPayForm(f => ({ ...f, status:'paid' }));
                        } else {
                          await invoiceAPI.updateStatus(selected._id, { status: v });
                          setSelected(s2 => ({ ...s2, status: v }));
                          load();
                          toast.success(`Status → ${s.label}`);
                        }
                      }}>
                      {selected.status===v?'✓ ':''}{s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform info */}
              {(selected.upworkContractId || selected.wiseTransferId || selected.platformRefId) && (
                <div style={{ ...S.card, padding:18 }}>
                  <div style={S.section}>Platform Details</div>
                  {selected.upworkContractId && <div style={{ fontSize:12, marginBottom:6 }}><span style={{ color:'#94a3b8' }}>Contract: </span><strong>{selected.upworkContractId}</strong></div>}
                  {selected.upworkJobTitle   && <div style={{ fontSize:12, marginBottom:6 }}><span style={{ color:'#94a3b8' }}>Job: </span><strong>{selected.upworkJobTitle}</strong></div>}
                  {selected.wiseTransferId   && <div style={{ fontSize:12, marginBottom:6 }}><span style={{ color:'#94a3b8' }}>Wise ID: </span><strong>{selected.wiseTransferId}</strong></div>}
                  {selected.platformRefId    && <div style={{ fontSize:12, marginBottom:6 }}><span style={{ color:'#94a3b8' }}>Ref: </span><strong>{selected.platformRefId}</strong></div>}
                  {selected.paymentNotes     && <div style={{ fontSize:12, color:'#64748b', marginTop:8 }}>{selected.paymentNotes}</div>}
                </div>
              )}

              {/* Partial Payments */}
              {selected.status !== 'cancelled' && (
                <div style={{ ...S.card, padding:18 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div style={S.section}>Payments Received</div>
                    {selected.status !== 'paid' && (
                      <button style={{ ...S.btn('success'), fontSize:11, padding:'5px 12px' }} onClick={()=>setPpModal(true)}>+ Add</button>
                    )}
                  </div>
                  {/* Progress bar */}
                  {parseFloat(selected.total) > 0 && (
                    <div style={{ marginBottom:12 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#64748b', marginBottom:4 }}>
                        <span>Paid: {FMT(parseFloat(selected.amountPaid||0), selected.currency)}</span>
                        <span>Total: {FMT(parseFloat(selected.total), selected.currency)}</span>
                      </div>
                      <div style={{ height:6, borderRadius:4, background:'#f1f5f9', overflow:'hidden' }}>
                        <div style={{ height:'100%', borderRadius:4, background:'#22c55e', width:`${Math.min(100, (parseFloat(selected.amountPaid||0)/parseFloat(selected.total))*100)}%`, transition:'width .4s' }}/>
                      </div>
                    </div>
                  )}
                  {(selected.partialPayments||[]).length === 0
                    ? <div style={{ fontSize:12, color:'#94a3b8', textAlign:'center', padding:'8px 0' }}>No payments recorded yet</div>
                    : (selected.partialPayments||[]).map((pp, i) => (
                      <div key={pp.id||i} style={{ padding:'9px 12px', background:'#f8fafc', borderRadius:9, marginBottom:6, fontSize:12 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, color:'#0f172a' }}>
                          <span>{FMT(pp.amount, selected.currency)}</span>
                          <span style={{ color:'#94a3b8', fontWeight:400 }}>{pp.date}</span>
                        </div>
                        {pp.method && <div style={{ color:'#64748b', marginTop:2 }}>{pp.method}</div>}
                        {pp.note   && <div style={{ color:'#94a3b8', marginTop:1 }}>{pp.note}</div>}
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Partial Payment Modal ────────────────────────────────────────── */}
      {ppModal && selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(4px)' }}
          onClick={()=>setPpModal(false)}>
          <div style={{ background:'#fff', borderRadius:22, padding:32, width:420, maxWidth:'93vw', boxShadow:'0 24px 80px #0003' }}
            onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:800, color:'#0f172a' }}>💵 Record Partial Payment</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
              <div>
                <label style={S.label}>Amount ({selected.currency})*</label>
                <input type="number" style={INP()} value={ppForm.amount} onChange={e=>setPpForm(f=>({...f,amount:e.target.value}))} placeholder={`e.g. ${parseFloat(selected.total)/2}`}/>
              </div>
              <div>
                <label style={S.label}>Payment Date</label>
                <input type="date" style={INP()} value={ppForm.date} onChange={e=>setPpForm(f=>({...f,date:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Method</label>
                <input style={INP()} value={ppForm.method} onChange={e=>setPpForm(f=>({...f,method:e.target.value}))} placeholder="e.g. Wise, PayPal, NEFT"/>
              </div>
              <div>
                <label style={S.label}>Note</label>
                <input style={INP()} value={ppForm.note} onChange={e=>setPpForm(f=>({...f,note:e.target.value}))} placeholder="Transaction ID, reference…"/>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
              <button style={S.btn('ghost')} onClick={()=>setPpModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={savePartialPayment} disabled={ppSaving}>{ppSaving?'Saving…':'✓ Record Payment'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Payment Modal ─────────────────────────────────────────────────── */}
      {payModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(4px)' }}
          onClick={()=>setPayModal(null)}>
          <div style={{ background:'#fff', borderRadius:22, padding:32, width:460, maxWidth:'93vw', boxShadow:'0 24px 80px #0003', animation:'fadeUp .2s ease' }}
            onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 6px', fontSize:19, fontWeight:800, color:'#0f172a' }}>✅ Mark Paid in Full</h3>
            <p style={{ margin:'0 0 20px', fontSize:13, color:'#64748b' }}>Records full payment and marks invoice as paid. For partial payments, use "Add Payment" in the invoice detail instead.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={S.label}>Payment Method</label>
                <input style={INP()} value={payForm.paymentMethod} onChange={e=>setPayForm(f=>({...f,paymentMethod:e.target.value}))} placeholder="e.g. Wise USD, PayPal, HDFC NEFT, UPI"/>
              </div>
              <div>
                <label style={S.label}>Payment Date</label>
                <input type="date" style={INP()} value={payForm.paidOn} onChange={e=>setPayForm(f=>({...f,paidOn:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Notes / Transaction ID (optional)</label>
                <textarea style={{ ...INP(), height:72, resize:'vertical' }} value={payForm.paymentNotes} onChange={e=>setPayForm(f=>({...f,paymentNotes:e.target.value}))} placeholder="Transaction ID, reference…"/>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
              <button style={S.btn('ghost')} onClick={()=>setPayModal(null)}>Cancel</button>
              <button style={S.btn('primary')} onClick={markPayment}>✓ Mark as Paid</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
